import { useIsMobile } from '@src/hooks/useIsMobile';
import { EButtonIntents } from '@src/typings';
import { Input } from 'antd';
import {
    CalendarPicker as CalendarPickerAntMobile,
    CalendarPickerProps,
    ConfigProvider,
} from 'antd-mobile';
import 'antd-mobile/es/global';
import enUS from 'antd-mobile/es/locales/en-US';
import type { RangePickerProps as AntdRangePickerProps } from 'antd/lib/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useState } from 'react';
import { BsCalendar2DateFill } from 'react-icons/bs';
import { StyledFormItem } from '../FileUpload/styles';
import { withTheme } from '../ThemeProvider/withTheme';
import {
    DatePickerDropdownStyles,
    StyledApplyButton,
    StyledCancelButton,
    StyledDatePicker,
    StyledFooterRow,
} from './styles';
import { PresetItem, PresetsDatePicker, RangeCalenderProps } from './types';

const RangeCalenderComponent: FC<RangeCalenderProps> = ({
    presets = PresetsDatePicker,
    value,
    defaultValue,
    name,
    placeholder,
    allowClear = true,
    disabled = false,
    format = 'YYYY-MM-DD',
    disabledDate,
    showToday = false,
    status,
    layoutLabel = 'vertical',
    size = 'middle',
    renderFooter,
    placement = 'bottomLeft',
    className,
    ref,
    style,
    label,
    onChange,
    formItemProps,
    inputMobileProps,
    required,
    singleMonthView,
    isWithoutFooter,
    open,
    onOpenChange,
    ...props
}) => {
    const isMobile = useIsMobile();
    //states
    const [selectionMobile, setSelectionMobile] = useState<[Date, Date]>(
        Array.isArray(value) && value.length === 2
            ? (value as [Date, Date])
            : [new Date(), new Date()]
    );
    const [tempSelectionMobile, setTempSelectionMobile] =
        useState(selectionMobile);

    const [selectionDesktop, setSelectionDesktop] = useState<[Dayjs, Dayjs]>(
        value
            ? (value as [Dayjs, Dayjs])
            : defaultValue
              ? (defaultValue as [Dayjs, Dayjs])
              : [dayjs(), dayjs()]
    );
    const [tempSelectionDesktop, setTempSelectionDesktop] =
        useState(selectionDesktop);

    const [openDesktop, setOpenDesktop] = useState(false);
    const [openMobile, setMobileOpen] = useState(false);

    const ranges = Object.fromEntries(
        presets.map((p: PresetItem) => [p.label, p.range])
    );

    // Desktop handlers
    const handleCalendarChangeDesktop: AntdRangePickerProps['onCalendarChange'] =
        (dates) => {
            if (Array.isArray(dates) && dates[0] && dates[1]) {
                setTempSelectionDesktop([dates[0], dates[1]]);
            }
            if (isWithoutFooter)
                onChange?.(
                    Array.isArray(dates) && dates[0] && dates[1]
                        ? [dates[0], dates[1]]
                        : null
                );
        };

    const handleChangeDesktop: AntdRangePickerProps['onChange'] = (dates) => {
        if (Array.isArray(dates) && dates[0] !== null && dates[1] !== null) {
            setTempSelectionDesktop([dates[0], dates[1]]);
        }
        if (!dates) onChange?.(null);
    };

    const handelCancelButtonDesktop = () => {
        setTempSelectionDesktop(selectionDesktop);
        setOpenDesktop(false);
        onOpenChange?.(false);
    };

    const handelApplyButtonDesktop = () => {
        setSelectionDesktop(tempSelectionDesktop);
        onChange?.(tempSelectionDesktop);
        setOpenDesktop(false);
    };

    // Mobile handlers
    const handleChangeCalendarMobile = (val: [Date, Date] | null) => {
        if (val && val.length === 2) {
            setTempSelectionMobile([val[0], val[1]]);
        }
    };

    const handleApplyButtonMobile = () => {
        setSelectionMobile(tempSelectionMobile);
        onChange?.(tempSelectionMobile);
        setMobileOpen(false);
    };

    const handleCancelButtonMobile = () => {
        setTempSelectionMobile(selectionMobile);
        setMobileOpen(false);
    };

    const Footer = (
        <StyledFooterRow>
            <StyledCancelButton
                intent={EButtonIntents.LightLinear}
                size="sm"
                onClick={handelCancelButtonDesktop}
                className="!rounded"
            >
                Cancel
            </StyledCancelButton>
            <StyledApplyButton
                intent={EButtonIntents.Filled}
                size="sm"
                onClick={handelApplyButtonDesktop}
                className="!rounded"
            >
                Apply
            </StyledApplyButton>
        </StyledFooterRow>
    );

    const CalenderMobile = (
        <StyledFormItem
            name={name}
            layout={layoutLabel}
            label={label}
            required={required}
        >
            <Input
                disabled={disabled}
                placeholder={placeholder}
                allowClear={allowClear}
                onClick={() => setMobileOpen(true)}
                suffix={
                    <button
                        className="bg-btnLightFilled rounded-sm p-1"
                        onClick={() => setMobileOpen(true)}
                    >
                        <BsCalendar2DateFill
                            color={props.theme?.colors.primary.main.border}
                        />
                    </button>
                }
                value={`${dayjs(tempSelectionMobile[0]).format(format)} – ${dayjs(
                    tempSelectionMobile[1]
                ).format(format)}`}
                {...inputMobileProps}
                onChange={(e) => inputMobileProps?.onChange?.(e.target.value)}
            />
            <ConfigProvider locale={enUS}>
                <CalendarPickerAntMobile
                    confirmText="Apply"
                    allowClear={allowClear}
                    {...(props as any as CalendarPickerProps)}
                    selectionMode="range"
                    className={className}
                    style={style}
                    value={tempSelectionMobile}
                    defaultValue={selectionMobile}
                    onMaskClick={handleCancelButtonMobile}
                    onClose={handleCancelButtonMobile}
                    onConfirm={handleApplyButtonMobile}
                    onChange={handleChangeCalendarMobile}
                    visible={openMobile}
                />
            </ConfigProvider>
        </StyledFormItem>
    );

    const CalendarDesktop = (
        <StyledFormItem
            name={name}
            layout={layoutLabel}
            label={label}
            required={required}
        >
            <DatePickerDropdownStyles />
            <StyledDatePicker
                {...props}
                disabled={disabled}
                allowClear={allowClear}
                placement={placement}
                status={status}
                size={size}
                format={format}
                disabledDate={disabledDate}
                className={className}
                style={style}
                open={open !== undefined ? open : openDesktop}
                onOpenChange={onOpenChange || setOpenDesktop}
                defaultValue={defaultValue as [Dayjs, Dayjs]}
                value={tempSelectionDesktop}
                onCalendarChange={handleCalendarChangeDesktop}
                onChange={handleChangeDesktop}
                ranges={ranges}
                suffixIcon={
                    <button
                        className="bg-btnLightFilled rounded-sm p-1"
                        onClick={() => setOpenDesktop(true)}
                    >
                        <BsCalendar2DateFill
                            color={props.theme?.colors.primary.main.border}
                        />
                    </button>
                }
                dropdownClassName="my-range-picker-dropdown"
                panelRender={(menu) => (
                    <div className="my-range-picker-dropdown">
                        {menu}
                        {!isWithoutFooter &&
                            (renderFooter ? renderFooter() : Footer)}
                    </div>
                )}
                popupClassName={singleMonthView ? 'dateRangePicker' : ''}
            />
        </StyledFormItem>
    );

    return isMobile ? CalenderMobile : CalendarDesktop;
};

export const RangeCalender = withTheme(RangeCalenderComponent);
